import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { Target, Trophy, Loader2, AlertTriangle, CheckCircle, RefreshCw, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BoardScoring() {
  const { boardId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedWinner, setSubmittedWinner] = useState("");
  const [nextMatch, setNextMatch] = useState(null);
  const [error, setError] = useState("");

  // Two-step flow: null = pick winner, then confirm with scores
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [scores, setScores] = useState({ hsPlayer: "", hsValue: "", lsPlayer: "", lsValue: "", hcPlayer: "", hcValue: "" });

  const fetchBoard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/boards/${boardId}/active-match`);
      setData(res.data);
      setError("");
    } catch {
      setError("Board not found.");
    }
    setLoading(false);
  }, [boardId]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // Auto-refresh every 10s when no match or waiting
  useEffect(() => {
    if (submitted || selectedWinner) return;
    const interval = setInterval(fetchBoard, 10000);
    return () => clearInterval(interval);
  }, [fetchBoard, submitted, selectedWinner]);

  const allPlayers = () => {
    if (!data?.match) return [];
    const players = [];
    const m = data.match;
    if (m.team1_players) m.team1_players.split(" & ").forEach(p => players.push(p.trim()));
    if (m.team2_players) m.team2_players.split(" & ").forEach(p => players.push(p.trim()));
    // Fallback for singles
    if (players.length === 0) {
      if (m.team1_name) players.push(m.team1_name);
      if (m.team2_name) players.push(m.team2_name);
    }
    return players.filter(Boolean);
  };

  const submitResult = async () => {
    if (!selectedWinner) return;
    setSubmitting(true); setError("");
    const payload = { winner_team_id: selectedWinner.id };
    if (scores.hsPlayer && scores.hsValue) {
      payload.high_score_player = scores.hsPlayer;
      payload.high_score_value = parseInt(scores.hsValue);
    }
    if (scores.lsPlayer && scores.lsValue) {
      payload.low_score_player = scores.lsPlayer;
      payload.low_score_value = parseInt(scores.lsValue);
    }
    if (scores.hcPlayer && scores.hcValue) {
      payload.high_checkout_player = scores.hcPlayer;
      payload.high_checkout_value = parseInt(scores.hcValue);
    }
    try {
      const res = await axios.post(`${API}/boards/${boardId}/submit-result`, payload);
      setSubmitted(true);
      setSubmittedWinner(selectedWinner.name);
      setNextMatch(res.data.next_match || null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit result.");
    }
    setSubmitting(false);
  };

  const resetForNextMatch = () => {
    setSubmitted(false);
    setSubmittedWinner("");
    setSelectedWinner(null);
    setScores({ hsPlayer: "", hsValue: "", lsPlayer: "", lsValue: "", hcPlayer: "", hcValue: "" });
    setNextMatch(null);
    setLoading(true);
    fetchBoard();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#E3292E] animate-spin" />
      </div>
    );
  }

  if (error === "Board not found.") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertTriangle className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <h1 className="font-heading text-white text-xl mb-2">Board Not Found</h1>
          <p className="text-white/50 text-sm font-body">This board doesn't exist or the QR code is invalid.</p>
        </div>
      </div>
    );
  }

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-sm w-full">
          <div className="w-16 h-16 rounded-full bg-[#309F6A]/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-[#309F6A]" />
          </div>
          <h1 className="font-heading text-white text-2xl mb-2">Result Submitted!</h1>
          <p className="text-white/60 text-sm font-body mb-6">
            <span className="text-white font-bold">{submittedWinner}</span> wins!
          </p>
          {nextMatch ? (
            <div className="border border-white/10 bg-[#141414] p-4 mb-4 text-left">
              <p className="text-[#E3292E] text-xs uppercase tracking-[0.15em] font-heading mb-2">Next Match Loading...</p>
              <p className="text-white font-heading font-bold">{nextMatch.team1_name} vs {nextMatch.team2_name}</p>
              <p className="text-white/40 text-xs font-body mt-1">{nextMatch.team1_players} vs {nextMatch.team2_players}</p>
            </div>
          ) : (
            <div className="border border-white/10 bg-[#141414] p-4 mb-4">
              <p className="text-white/50 text-sm font-body">No more matches queued for this board.</p>
            </div>
          )}
          <Button onClick={resetForNextMatch} className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading h-12" data-testid="board-next-match-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> {nextMatch ? "GO TO NEXT MATCH" : "CHECK FOR MATCHES"}
          </Button>
          <p className="text-white/20 text-xs mt-6 font-body">Darts Manager &middot; {data?.board?.label}</p>
        </div>
      </div>
    );
  }

  const board = data?.board;
  const match = data?.match;
  const event = data?.event;

  // No active match
  if (!match || match.status === "completed") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-sm w-full">
          <Target className="h-10 w-10 text-[#E3292E] mx-auto mb-3" />
          <h1 className="font-heading text-white text-xl mb-2">{board?.label || "Board"}</h1>
          <p className="text-white/40 text-xs font-body mb-1">{data?.venue_name}</p>
          <div className="border border-white/10 bg-[#141414] p-6 mt-4">
            <p className="text-white/50 text-sm font-body">No active match on this board right now.</p>
            <p className="text-white/30 text-xs font-body mt-2">This page auto-refreshes when a new match is assigned.</p>
          </div>
          <Button onClick={fetchBoard} variant="outline" className="mt-4 border-white/20 text-white/60 hover:bg-white/10 rounded-none font-heading" data-testid="board-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> REFRESH
          </Button>
          <p className="text-white/20 text-xs mt-6 font-body">Darts Manager</p>
        </div>
      </div>
    );
  }

  const players = allPlayers();

  // Step 2: Confirm winner + enter scores
  if (selectedWinner) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          {/* Header */}
          <div className="text-center mb-4">
            <Target className="h-8 w-8 text-[#E3292E] mx-auto mb-2" />
            <h1 className="font-heading text-white text-lg">{board?.label}</h1>
          </div>

          {/* Winner confirmation */}
          <div className="border border-[#309F6A]/30 bg-[#309F6A]/10 p-4 mb-5 text-center">
            <Trophy className="h-5 w-5 text-[#309F6A] mx-auto mb-1" />
            <p className="text-white font-heading font-bold text-lg">{selectedWinner.name} wins!</p>
            <p className="text-white/40 text-xs font-body">{selectedWinner.players}</p>
          </div>

          {/* Notable Scores (optional) */}
          <div className="space-y-3 mb-5">
            <p className="text-white/50 text-xs uppercase tracking-[0.15em] font-heading">Notable Scores (optional)</p>

            {/* High Score */}
            <div className="border border-white/10 bg-[#141414] p-3">
              <p className="text-white/40 text-xs font-heading mb-2">Highest Score</p>
              <div className="flex gap-2">
                <Select value={scores.hsPlayer} onValueChange={(v) => setScores(s => ({ ...s, hsPlayer: v }))}>
                  <SelectTrigger className="flex-1 bg-transparent border-white/10 text-white rounded-none text-sm h-10" data-testid="board-hs-player">
                    <SelectValue placeholder="Player" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 rounded-none">
                    {players.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="Score" value={scores.hsValue} onChange={e => setScores(s => ({ ...s, hsValue: e.target.value }))} className="w-24 bg-transparent border-white/10 text-white rounded-none text-sm h-10" data-testid="board-hs-value" />
              </div>
            </div>

            {/* Low Score */}
            <div className="border border-white/10 bg-[#141414] p-3">
              <p className="text-white/40 text-xs font-heading mb-2">Lowest Score</p>
              <div className="flex gap-2">
                <Select value={scores.lsPlayer} onValueChange={(v) => setScores(s => ({ ...s, lsPlayer: v }))}>
                  <SelectTrigger className="flex-1 bg-transparent border-white/10 text-white rounded-none text-sm h-10" data-testid="board-ls-player">
                    <SelectValue placeholder="Player" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 rounded-none">
                    {players.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="Score" value={scores.lsValue} onChange={e => setScores(s => ({ ...s, lsValue: e.target.value }))} className="w-24 bg-transparent border-white/10 text-white rounded-none text-sm h-10" data-testid="board-ls-value" />
              </div>
            </div>

            {/* Highest Checkout */}
            <div className="border border-white/10 bg-[#141414] p-3">
              <p className="text-white/40 text-xs font-heading mb-2">Highest Checkout</p>
              <div className="flex gap-2">
                <Select value={scores.hcPlayer} onValueChange={(v) => setScores(s => ({ ...s, hcPlayer: v }))}>
                  <SelectTrigger className="flex-1 bg-transparent border-white/10 text-white rounded-none text-sm h-10" data-testid="board-hc-player">
                    <SelectValue placeholder="Player" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 rounded-none">
                    {players.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="Score" value={scores.hcValue} onChange={e => setScores(s => ({ ...s, hcValue: e.target.value }))} className="w-24 bg-transparent border-white/10 text-white rounded-none text-sm h-10" data-testid="board-hc-value" />
              </div>
            </div>
          </div>

          {error && <p className="text-[#E3292E] text-sm text-center mb-3 font-body" data-testid="board-error">{error}</p>}

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={() => { setSelectedWinner(null); setScores({ hsPlayer: "", hsValue: "", lsPlayer: "", lsValue: "", hcPlayer: "", hcValue: "" }); }} variant="outline" className="border-white/20 text-white/60 hover:bg-white/10 rounded-none font-heading h-12" data-testid="board-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Button onClick={submitResult} disabled={submitting} className="flex-1 bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading h-12 text-base" data-testid="board-confirm-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {submitting ? "SUBMITTING..." : "CONFIRM RESULT"}
            </Button>
          </div>

          <p className="text-white/20 text-xs text-center mt-6 font-body">Darts Manager &middot; Scores are optional</p>
        </div>
      </div>
    );
  }

  // Step 1: Pick the winner
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-4">
          <Target className="h-8 w-8 text-[#E3292E] mx-auto mb-2" />
          <h1 className="font-heading text-white text-lg tracking-wide">{board?.label || "Board"}</h1>
          <p className="text-white/40 text-xs font-body">{data?.venue_name}</p>
        </div>

        {/* Event Info */}
        {event && (
          <div className="text-center mb-6">
            <p className="text-white/60 text-sm font-heading">{event.name}</p>
            <div className="flex gap-2 justify-center mt-1">
              <Badge className="rounded-none bg-[#E3292E]/20 text-[#E3292E] text-xs">{event.game_format}</Badge>
              <Badge className="rounded-none bg-white/10 text-white/60 text-xs">{event.play_mode}</Badge>
            </div>
          </div>
        )}

        {/* Match: Who Won? */}
        <div className="space-y-3">
          <p className="text-center text-white/50 text-xs uppercase tracking-[0.2em] font-heading">Who won?</p>

          <Button
            onClick={() => setSelectedWinner({ id: match.team1_id, name: match.team1_name, players: match.team1_players })}
            className="w-full h-20 bg-[#141414] hover:bg-[#E3292E]/20 border border-white/10 hover:border-[#E3292E] text-white rounded-none font-heading text-lg transition-all duration-200 flex flex-col items-center justify-center gap-1"
            data-testid="board-team1-win-btn"
          >
            <Trophy className="h-5 w-5 text-[#E3292E]" />
            <span className="truncate max-w-full px-2">{match.team1_name}</span>
            {match.team1_players && <span className="text-white/40 text-xs font-body">{match.team1_players}</span>}
          </Button>

          <div className="text-center">
            <span className="text-white/20 text-xs font-heading">VS</span>
          </div>

          <Button
            onClick={() => setSelectedWinner({ id: match.team2_id, name: match.team2_name, players: match.team2_players })}
            className="w-full h-20 bg-[#141414] hover:bg-[#E3292E]/20 border border-white/10 hover:border-[#E3292E] text-white rounded-none font-heading text-lg transition-all duration-200 flex flex-col items-center justify-center gap-1"
            data-testid="board-team2-win-btn"
          >
            <Trophy className="h-5 w-5 text-[#E3292E]" />
            <span className="truncate max-w-full px-2">{match.team2_name}</span>
            {match.team2_players && <span className="text-white/40 text-xs font-body">{match.team2_players}</span>}
          </Button>
        </div>

        <p className="text-white/20 text-xs text-center mt-8 font-body">Darts Manager &middot; Tap the winning team</p>
      </div>
    </div>
  );
}
